import React from 'react';
import { useMemo } from 'react';
import { MAX_PRODUCTS } from 'components/Catalog/ProductGrid/CompareModal/constants';

/**
 * Shared hook for processing comparison SKUs
 * @param {Object} comparisonData - The comparison data object containing skus array
 * @returns {Array} Array of SKUs with length MAX_PRODUCTS
 */
export const useComparisonSkus = (comparisonData = {}) => {
    return useMemo(() => {
        return Array.from({ length: MAX_PRODUCTS }, (_, index) => {
            return comparisonData?.skus?.[index];
        });
    }, [comparisonData?.skus]);
};

/**
 * Validates if SKU and parent product exist
 * @param {Object} sku - The SKU object
 * @returns {Object|null} Returns { sku, parentProduct } if valid, null otherwise
 */
export const validateSkuAndParent = sku => {
    if (!sku) {
        return null;
    }

    const parentProduct = sku?.parentProducts?.[0];

    if (!parentProduct) {
        return null;
    }

    return { sku, parentProduct };
};

/**
 * Processes comparison values (colors, sizes, etc.)
 * @param {Object} sku - The SKU object
 * @param {Function} getValue - Function to extract value from SKU (e.g., sku => sku.color)
 * @param {Function} shouldShow - Function to determine if we should show the data
 * @returns {Object|null} Returns { currentValue, uniqueValues, totalValues, additionalValues, validatedSku, parentProduct } or null
 */
export const processComparisonValue = (sku, getValue, shouldShow) => {
    const validated = validateSkuAndParent(sku);

    if (!validated) {
        return null;
    }

    const { sku: validatedSku, parentProduct } = validated;

    // Get current value from SKU
    const currentValue = getValue(validatedSku) || '';

    // Collect all unique values from child SKUs
    const childSkus = parentProduct.skus || [];
    const uniqueValues = new Set(childSkus.map(childSku => getValue(childSku)).filter(Boolean));

    // Include current SKU's value
    if (currentValue) {
        uniqueValues.add(currentValue);
    }

    // Check if we should show this data
    const shouldShowData = shouldShow ? shouldShow(validatedSku, parentProduct, uniqueValues, currentValue) : uniqueValues.size > 0;

    if (!shouldShowData || !currentValue) {
        return null;
    }

    // Calculate additional values count
    const totalValues = uniqueValues.size;
    const additionalValues = totalValues > 1 ? totalValues - 1 : 0;

    return {
        currentValue,
        uniqueValues,
        totalValues,
        additionalValues,
        validatedSku,
        parentProduct
    };
};
